import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase/app';
import { MessageTemplate } from '../../../shared/models';

const COLLECTION_NAME = 'venue_message_templates';

/**
 * Get all message templates for a specific venue
 * @param venueId The ID of the venue
 * @param category Optional category to filter templates
 * @returns Promise that resolves to an array of MessageTemplate objects
 */
export async function getVenueMessageTemplates(venueId: string, category?: string): Promise<MessageTemplate[]> {
  try {
    let templateQuery = query(
      collection(db, COLLECTION_NAME),
      where('VenueID', '==', venueId)
    );
    
    if (category) {
      templateQuery = query(
        templateQuery,
        where('Category', '==', category)
      );
    }
    
    const querySnapshot = await getDocs(templateQuery);
    
    return querySnapshot.docs.map(doc => ({
      ID: doc.id,
      ...doc.data()
    } as MessageTemplate));
  } catch (error) {
    console.error('Error getting message templates:', error);
    throw error;
  }
}

/**
 * Get a single message template by ID
 * @param templateId The ID of the template to get
 * @returns Promise that resolves to a MessageTemplate or null if not found
 */
export async function getMessageTemplateById(templateId: string): Promise<MessageTemplate | null> {
  try {
    const docRef = doc(db, COLLECTION_NAME, templateId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return {
      ID: docSnap.id,
      ...docSnap.data()
    } as MessageTemplate;
  } catch (error) {
    console.error('Error getting message template:', error);
    throw error;
  }
}

/**
 * Create a new message template
 * @param template The template data to create
 * @returns Promise that resolves to the ID of the new template
 */
export async function createMessageTemplate(template: Omit<MessageTemplate, 'ID'>): Promise<string> {
  try {
    // Convert date objects to ISO strings to conform with security rules
    const templateToSave = {
      ...template,
      // Ensure dates are stored as strings, as expected by the security rules
      CreatedAt: typeof template.CreatedAt === 'string' ? template.CreatedAt : new Date(template.CreatedAt).toISOString(),
      LastUpdatedAt: typeof template.LastUpdatedAt === 'string' ? template.LastUpdatedAt : new Date(template.LastUpdatedAt).toISOString()
    };
    
    const docRef = await addDoc(collection(db, COLLECTION_NAME), templateToSave);
    return docRef.id;
  } catch (error) {
    console.error('Error creating message template:', error);
    throw error;
  }
}

/**
 * Update an existing message template
 * @param templateId The ID of the template to update
 * @param templateData The new template data
 * @returns Promise that resolves when the update is complete
 */
export async function updateMessageTemplate(
  templateId: string, 
  templateData: Partial<Omit<MessageTemplate, 'ID' | 'VenueID' | 'CreatedAt'>>
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, templateId);
    
    // Always update the LastUpdatedAt timestamp as ISO string
    const dataWithTimestamp = {
      ...templateData,
      LastUpdatedAt: new Date().toISOString()
    };
    
    await updateDoc(docRef, dataWithTimestamp);
  } catch (error) {
    console.error('Error updating message template:', error);
    throw error;
  }
}

/**
 * Delete a message template
 * @param templateId The ID of the template to delete
 * @returns Promise that resolves when the delete is complete
 */
export async function deleteMessageTemplate(templateId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, templateId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting message template:', error);
    throw error;
  }
}

/**
 * Get template variables that can be used in a message template
 * @returns Object with keys as variable names and values as descriptions
 */
export function getAvailableTemplateVariables(): Record<string, string> {
  return {
    '{{venueName}}': 'Name of your venue',
    '{{sectionName}}': 'Name of the section',
    '{{date}}': 'Event date',
    '{{startTime}}': 'Start time',
    '{{endTime}}': 'End time',
    '{{guestCount}}': 'Number of guests',
    '{{basePrice}}': 'Base price amount',
    '{{totalPrice}}': 'Total price including fees',
    '{{depositAmount}}': 'Deposit amount (if applicable)',
  };
}

/**
 * Apply template variables to a template string
 * @param template The template string
 * @param variables Object containing variable values to substitute
 * @returns The template with variables replaced with their values
 */
export function applyTemplateVariables(
  template: string, 
  variables: Record<string, string | number>
): string {
  let result = template;
  
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    result = result.replace(regex, String(value));
  });
  
  return result;
} 